import { hasDeBetterDocRecommendedCareProviders } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-recommended-care-providers"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { getDocumentsByCountryFragment } from "@/modules/patients/detail/store/useDocumentStore"
import { DocumentType } from "@/modules/patients/types/ProgressNoteDocument"

type DocumentsToSendCount = {
  numberOfDocumentsToSend: number
  numberOfDocumentsToSendToPatient: number
  numberOfDocumentsRequiringPatientChannel: number
}

const getDocumentTypesToSendForProgressNote = (data: ProgressNoteData): DocumentType[] => {
  const documentTypesToSend = getDocumentsByCountryFragment(data)
    .filter((document) => document.concludedBy === null && document.type !== "medicalReportAttachments")
    .map((document) => document.type)

  const carePlanSummary = data.carePlanSummary?.edges?.at(0)?.node
  if (data.treatmentPlan && !carePlanSummary?.concludedBy) {
    documentTypesToSend.push("carePlanSummary")
  }

  return documentTypesToSend
}

const isDocumentTypeSendableToPatient = (documentType: DocumentType, progressNote: ProgressNoteData): boolean => {
  if (documentType === "referralLetter") return hasDeBetterDocRecommendedCareProviders(progressNote)

  return documentType !== "ch_consultationReport"
}

const isDocumentTypeRequiringPatientChannel = (documentType: DocumentType, progressNote: ProgressNoteData): boolean =>
  isDocumentTypeSendableToPatient(documentType, progressNote) && documentType !== "carePlanSummary"

export const getNumberOfDocumentsToSend = (progressNotes: ProgressNoteData[]): DocumentsToSendCount =>
  progressNotes.reduce<DocumentsToSendCount>(
    (counts, progressNote) => {
      const documentTypesToSend = getDocumentTypesToSendForProgressNote(progressNote)
      return {
        numberOfDocumentsToSend: counts.numberOfDocumentsToSend + documentTypesToSend.length,
        numberOfDocumentsToSendToPatient:
          counts.numberOfDocumentsToSendToPatient +
          documentTypesToSend.filter((documentType) => isDocumentTypeSendableToPatient(documentType, progressNote))
            .length,
        numberOfDocumentsRequiringPatientChannel:
          counts.numberOfDocumentsRequiringPatientChannel +
          documentTypesToSend.filter((documentType) =>
            isDocumentTypeRequiringPatientChannel(documentType, progressNote),
          ).length,
      }
    },
    { numberOfDocumentsToSend: 0, numberOfDocumentsToSendToPatient: 0, numberOfDocumentsRequiringPatientChannel: 0 },
  )
